import { X, Camera, Send } from "lucide-react";
import React, { useState } from "react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";
interface ReportBugProps {
  partnerId?: string;
  contact?: string;
}

export const ReportBug: React.FC<ReportBugProps> = ({ partnerId, contact }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const {t} = useTranslation()
  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const getImage = () => {
    const element = document.getElementById("contentSgb");
    html2canvas(element || document.body, {
      logging: true,
      useCORS: true,
      allowTaint: true,
      scale: 2,
      backgroundColor: null,
      imageTimeout: 2000,
    })
      .then(async (canvas) => {
        const base64Image = canvas.toDataURL();
        setScreenshot(base64Image);
        toast.success(t("toast.captureSuccess"))
      })
      .catch((error) => {
        console.error("Error capture:", error);
        toast.error(t("toast.captureFailed"))
      });
  };

  const sendReport = async () => {
    const formData = new FormData();
    formData.append("title", title);
    if (contact) {
      formData.append("email", contact);
    }
  
    formData.append("description", description);
    if (screenshot) {
      formData.append("screenshot", screenshot);
    }
   
    try {
      await axios.post("/api/uploadBugReport", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      closeModal()
      toast.success(t("toast.successReportBug"));
      setDescription("")
      setScreenshot("")
      setTitle("")
      
    } catch (error) {
      console.error("Error sending report:", error);
      toast.error(t("toast.failReportBug"));
    }
  };
  
  const removeScreenshot = () => {
    setScreenshot(null);
  };

  return (
    <div className="group fixed bottom-20 right-0 p-2 flex items-end justify-end w-10 h-10 ">
      <div
        className="text-white shadow-xl flex items-center justify-center p-2 rounded-full bg-gradient-to-r from-gray-500 to-gray-900 z-50 absolute cursor-pointer"
        onClick={openModal}
      >
        <svg
          className="w-4 h-4 group-hover:rotate-180 transition-all"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m8 2 1.88 1.88" />
          <path d="M14.12 3.88 16 2" />
          <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
          <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
          <path d="M12 20v-9" />
          <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
          <path d="M6 13H2" />
          <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
          <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
          <path d="M22 13h-4" />
          <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
        </svg>
      </div>

      {isOpen && (
        <div
          className="fixed bottom-32 right-4 bg-white w-[300px] h-auto rounded-2xl shadow-xl p-6 z-100 transition-all duration-300 transform"
          style={{
            transform: isOpen ? "translateY(0)" : "translateY(100%)",
          }}
        >
          <div
            className="absolute -bottom-2 right-2 w-0 h-0 border-l-[12px] border-r-[12px] border-t-[12px] border-l-transparent border-r-transparent border-t-white"
          />
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-lg">{t("bug_report.report")}</h3>
            <X className="cursor-pointer" onClick={closeModal}></X>
          </div>
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-2">{t("bug_report.title")}</label>
              <input
                type="text"
                className="p-2 border border-gray-300 rounded-xl"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-2">{t("bug_report.description")}</label>
              <textarea
                placeholder=""
                className="w-full h-32 p-2 border border-gray-300 rounded-xl mb-4"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <button
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-400"
              onClick={getImage}
            >
              <div className="flex items-center justify-center space-x-2">
                <Camera size={16} />
                <p className="font-semibold text-sm">{t("bug_report.screenshot")}</p>
              </div>
            </button>

            {screenshot && (
              <div className="mt-4 relative">
                <img
                  src={screenshot}
                  alt="Screenshot"
                  className="w-full h-auto rounded-lg"
                />
                <X
                  className="absolute top-0 right-0 text-red-500 cursor-pointer"
                  onClick={removeScreenshot}
                  size={20}
                />
              </div>
            )}

            <button
              className="bg-black text-white px-4 py-2 rounded-xl hover:bg-gray-800"
              onClick={sendReport}
            >
              <div className="flex items-center justify-center space-x-2">
                <Send size={16} />
                <p className="font-semibold text-sm">{t("bug_report.send")}</p>
              </div>
            </button>
            <button
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-400"
              onClick={closeModal}
            >
              <div className="flex items-center justify-center space-x-2">
                <X size={16} />
                <p className="font-semibold text-sm">{t("bug_report.cancel")}</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
